import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, PenLine, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VERIFICATION_METHODS = [
  { code: 'customer_rep_in_person', label: 'Customer representative — in person' },
  { code: 'customer_signature', label: 'Customer signature' },
  { code: 'customer_email', label: 'Customer email confirmation' },
  { code: 'customer_phone', label: 'Customer telephone confirmation' },
  { code: 'tfs_ops_manager', label: 'TFS Operations Manager verification' },
];
const CUSTOMER_METHODS = ['customer_rep_in_person', 'customer_signature', 'customer_email', 'customer_phone'];

const MiniSignaturePad = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const c = ref.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const c = ref.current.getContext('2d'); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext('2d'); const p = pos(e); c.lineTo(p.x, p.y); c.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL('image/png')); };
  const clear = () => { const c = ref.current.getContext('2d'); c.clearRect(0, 0, ref.current.width, ref.current.height); onChange(''); };
  useEffect(() => { const c = ref.current.getContext('2d'); c.strokeStyle = '#0E2A47'; c.lineWidth = 2; c.lineCap = 'round'; }, []);
  return (
    <div>
      <canvas ref={ref} width={640} height={160} data-testid="verify-signature-canvas"
        className="w-full rounded-md border border-slate-300 bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button type="button" onClick={clear} className="mt-1 text-xs text-slate-500 underline" data-testid="verify-signature-clear">Clear</button>
    </div>
  );
};

const SignaturePreview = ({ verification, workOrderId }) => {
  const { token } = useContext(AuthContext);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke;
    (async () => {
      if (verification?.signature_data) { setUrl(verification.signature_data); return; }
      if (verification?.signature_url) {
        try {
          const r = await axios.get(`${API}/work-orders/${verification.signature_url}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
          revoke = URL.createObjectURL(r.data); setUrl(revoke);
        } catch { /* ignore */ }
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [verification, token]);
  if (!verification?.has_signature) return null;
  if (verification.signature_kind === 'uploaded' && !url) return <div className="mt-1 text-xs text-slate-500">Signed document on file.</div>;
  return url ? <img src={url} alt="signature" data-testid="verify-signature-preview" className="mt-1 h-14 rounded border bg-white" /> : null;
};

export const VerificationCapture = ({ workOrderId, verification, canEdit, onSaved }) => {
  const { token, user } = useContext(AuthContext);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ method: '', verified_by: '', verified_by_title: '', customer_company: '', verified_at: '', confirmation_notes: '', signature_data: '', signature_upload: '', signature_content_type: '' });

  const isCustomer = CUSTOMER_METHODS.includes(f.method);
  const isOM = f.method === 'tfs_ops_manager';

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setF((p) => ({ ...p, signature_upload: reader.result, signature_content_type: file.type, signature_data: '' }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!f.method) { toast.error('Select a verification method'); return; }
    if (isCustomer && !f.verified_by.trim()) { toast.error('Representative / signer name is required'); return; }
    if (f.method === 'customer_signature' && !f.signature_data && !f.signature_upload) { toast.error('A signature is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/work-orders/${workOrderId}/verification`, {
        method: f.method,
        verified_by: isOM ? (user?.name || null) : (f.verified_by || null),
        verified_by_title: f.verified_by_title || null,
        customer_company: f.customer_company || null,
        verified_at: f.verified_at ? new Date(f.verified_at).toISOString() : null,
        confirmation_notes: f.confirmation_notes || null,
        signature_data: f.signature_data || null,
        signature_upload: f.signature_upload || null,
        signature_content_type: f.signature_content_type || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Verification recorded');
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record verification'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-lg border border-slate-200 p-3" data-testid="verification-capture">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <ShieldCheck size={14} /> Customer Verification
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="verify-edit-btn">
            {verification ? 'Correct verification' : 'Record verification'}
          </Button>
        )}
      </div>

      {verification && !editing && (
        <div className="space-y-1 text-xs" data-testid="verification-summary">
          <div className="flex justify-between gap-2"><span className="text-slate-400">Method</span><span className="text-right font-medium text-slate-700">{verification.method}</span></div>
          <div className="flex justify-between gap-2"><span className="text-slate-400">Verified by</span><span className="text-right font-medium text-slate-700">{verification.verified_by || '—'}{verification.verified_by_title ? `, ${verification.verified_by_title}` : ''}{verification.verified_by_role ? ` · ${verification.verified_by_role}` : ''}</span></div>
          {verification.customer_company && <div className="flex justify-between gap-2"><span className="text-slate-400">Company</span><span className="text-right font-medium text-slate-700">{verification.customer_company}</span></div>}
          {verification.verified_at && <div className="flex justify-between gap-2"><span className="text-slate-400">Date</span><span className="text-right font-medium text-slate-700">{verification.verified_at}</span></div>}
          <div className="flex justify-between gap-2"><span className="text-slate-400">Recorded by</span><span className="text-right font-medium text-slate-700">{verification.recorded_by_name || '—'}{verification.recorded_by_role ? ` (${verification.recorded_by_role})` : ''}</span></div>
          {verification.confirmation_notes && <div className="rounded bg-slate-50 px-2 py-1 text-slate-600"><span className="text-slate-400">Internal notes: </span>{verification.confirmation_notes}</div>}
          <SignaturePreview verification={verification} workOrderId={workOrderId} />
        </div>
      )}

      {!verification && !editing && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700" data-testid="verification-none">
          <AlertTriangle size={13} /> No verification recorded yet — required before billing approval.
        </div>
      )}

      {editing && (
        <div className="space-y-2" data-testid="verification-form">
          <div>
            <Label className="text-xs">Verification method *</Label>
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
              <SelectTrigger data-testid="verify-method-select"><SelectValue placeholder="Select method" /></SelectTrigger>
              <SelectContent>
                {VERIFICATION_METHODS.map((m) => <SelectItem key={m.code} value={m.code} data-testid={`verify-method-${m.code}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {isOM && (
            <div className="rounded bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">
              Verified by <b>{user?.name}</b> (authenticated TFS user), based on completed work, notes, and photographs.
            </div>
          )}

          {isCustomer && (
            <>
              <div className="grid gap-2 sm:grid-cols-2">
                <div><Label className="text-xs">Signer / representative name *</Label><Input value={f.verified_by} onChange={(e) => setF({ ...f, verified_by: e.target.value })} data-testid="verify-name" /></div>
                <div><Label className="text-xs">Title</Label><Input value={f.verified_by_title} onChange={(e) => setF({ ...f, verified_by_title: e.target.value })} data-testid="verify-title" /></div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <div><Label className="text-xs">Company / customer</Label><Input value={f.customer_company} onChange={(e) => setF({ ...f, customer_company: e.target.value })} data-testid="verify-company" /></div>
                <div><Label className="text-xs">Date &amp; time</Label><Input type="datetime-local" value={f.verified_at} onChange={(e) => setF({ ...f, verified_at: e.target.value })} data-testid="verify-datetime" /></div>
              </div>
            </>
          )}

          {f.method === 'customer_signature' && (
            <div className="rounded-md border border-slate-200 p-2">
              <div className="mb-1 flex items-center gap-1 text-xs text-slate-500"><PenLine size={12} /> Draw signature</div>
              <MiniSignaturePad onChange={(sig) => setF({ ...f, signature_data: sig, signature_upload: '', signature_content_type: '' })} />
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-200 px-2 py-1 hover:bg-slate-50" data-testid="verify-upload-label">
                  <Upload size={12} /> Upload signed document/photo
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onUpload} data-testid="verify-upload-input" />
                </label>
                {f.signature_upload && <span className="text-emerald-600">File attached</span>}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Confirmation notes (internal)</Label>
            <Textarea rows={2} value={f.confirmation_notes} onChange={(e) => setF({ ...f, confirmation_notes: e.target.value })} data-testid="verify-notes" />
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={busy} data-testid="verify-save-btn"><CheckCircle2 size={14} className="mr-1" /> Save verification</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationCapture;
